import { useEffect, useState } from "react";
import {
  ApiError,
  deleteAudit,
  listAudits,
  setRestartAllowed,
  type AuditSummary,
} from "../api/client";
import { TextField } from "../components/TextField";

/**
 * Screen 23 — the audits list.
 *
 * Six columns, and the last two are two columns on purpose. **Status** is where the audit
 * has reached; **Step 2 approvals** is who has agreed to the determination, and dual
 * approval is two decisions by two parties. Collapsing them into one pill would let the
 * certification body's confirmation read as the lead auditor's, which is the fact M3-2
 * exists to record separately.
 *
 * **The fourth row is a draft, and it is not an audit.** The prototype draws
 * `AUD-2026-0039 · Meridian Logistics · No standards selected · — · — · Draft intake`.
 * `audits` has `NOT NULL` on the certification scope, the products, the planned date and
 * the lead auditor — data dictionary §8 names the first three — so a part-typed intake is
 * a row in `audit_intake_drafts` and comes back flagged. It carries no reference, because
 * a reference is issued when the audit exists and not when somebody starts typing.
 *
 * Rows open what they name: an audit opens its determination (screen 30), a draft opens the
 * form it came from (screen 49). The prototype opens a draft onto a *determination* — nought
 * effective personnel against band 1551–2025 — and there is no determination for an intake
 * that was never run.
 *
 * ⚠ **A seventh column is not on the prototype, and it is here on purpose.** *Restart* is a
 * testing affordance: there is one session per audit and no reopening, so an audit day cannot
 * be rehearsed twice, and a tick here lets the lead auditor put that audit back to the state
 * its plan was issued in. It sits on this screen rather than the auditor's because it is the
 * **only** control in front of a purge with no ceiling — a nominated audit can be emptied at
 * any state, a signed and issued report included, along with the transcript, every finding
 * and the audit's own trail. Two further gates were offered on 27 August 2026 and declined:
 * a stop at the first report signature, and an environment switch keeping the capability out
 * of production. So the party that decides is deliberately not the party that presses.
 *
 * ⚠ **An eighth column is not on the prototype either.** *Delete* is a real, irrecoverable
 * removal — every row in Postgres, the audit's documents in S3 and its Bedrock knowledge
 * base — available in any state, with no exception for a signed or issued report. Requested
 * as a hard delete after the tradeoff was named. The typed reference is the whole of the
 * control, the same posture Restart's checkbox takes.
 */
export function AuditsList({
  onOpenAudit,
  onOpenDraft,
  onNewAudit,
}: {
  onOpenAudit: (auditId: string) => void;
  onOpenDraft: (draftId: string) => void;
  onNewAudit: () => void;
}) {
  const [audits, setAudits] = useState<AuditSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listAudits()
      .then(setAudits)
      .catch((cause: unknown) =>
        setError(cause instanceof ApiError ? cause.message : "The audits could not be read."),
      );
  }, []);

  const open = (audit: AuditSummary) =>
    audit.is_draft ? onOpenDraft(audit.id) : onOpenAudit(audit.id);

  return (
    <>
      <div className="between" style={{ marginBottom: "20px" }}>
        <div>
          <div className="eyebrow">Certification body</div>
          <h1>Audits</h1>
        </div>
        <button type="button" className="btn" onClick={onNewAudit}>
          Start new audit
        </button>
      </div>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="tscroll">
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Client organisation</th>
                <th>Audit date</th>
                <th>Lead auditor</th>
                <th>Status</th>
                <th>Step 2 approvals</th>
                <th>Restart</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {audits === null ? (
                <tr>
                  <td colSpan={8}>Reading the audits&hellip;</td>
                </tr>
              ) : audits.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    No audit has been created yet. Intake opens the first one.
                  </td>
                </tr>
              ) : (
                audits.map((audit) => (
                  <tr
                    key={audit.id}
                    className="rowlink"
                    onClick={() => open(audit)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        open(audit);
                      }
                    }}
                    tabIndex={0}
                    role="link"
                  >
                    <td>
                      <span className="mono">{audit.reference ?? "—"}</span>
                    </td>
                    <td>
                      <b>{audit.client_organisation}</b>
                      <br />
                      <span className="sub">
                        {(audit.standards ?? []).length === 0
                          ? "No standards selected"
                          : (audit.standards ?? []).join(" · ")}
                      </span>
                    </td>
                    <td>{audit.planned_date ?? "—"}</td>
                    <td>{audit.lead_auditor ?? "—"}</td>
                    <td>
                      <State
                        state={audit.state}
                        auditDay={audit.audit_day}
                        documentsFiled={audit.documents_filed ?? null}
                      />
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {audit.is_draft ? (
                        // Not "pending". An intake nobody has finished has nothing to
                        // approve, and a pending pill would read as waiting on somebody.
                        <span className="sub">—</span>
                      ) : (
                        <>
                          <Approval party="CB" at={audit.cb_approved_at ?? null} />{" "}
                          <Approval party="LA" at={audit.lead_auditor_approved_at ?? null} />
                        </>
                      )}
                    </td>
                    <td>
                      {audit.is_draft ? (
                        <span className="sub">—</span>
                      ) : (
                        <Restart
                          audit={audit}
                          onChanged={(allowed) =>
                            setAudits((rows) =>
                              (rows ?? []).map((row) =>
                                row.id === audit.id
                                  ? { ...row, restart_allowed: allowed }
                                  : row,
                              ),
                            )
                          }
                          onError={setError}
                        />
                      )}
                    </td>
                    <td>
                      {audit.is_draft ? (
                        <span className="sub">—</span>
                      ) : (
                        <Delete
                          audit={audit}
                          onDeleted={() =>
                            setAudits((rows) =>
                              (rows ?? []).filter((row) => row.id !== audit.id),
                            )
                          }
                          onError={setError}
                        />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/**
 * The eighteen `audit_state` values, in the words screen 23 uses for the six it shows.
 *
 * The colour carries meaning the label repeats rather than replaces: red is the state that
 * needs the certification body to act again, amber is waiting on somebody, green is
 * running. `reconfirmation` is red because a determination has moved under an approval
 * that was already given, and it is the one row on the prototype that is not a queue.
 *
 * **Two of the eighteen are not read from the state alone.** `awaiting_documents` and
 * `session_scheduled` are where an issued audit waits for the client, and the state does not
 * move while it waits: the documents arriving writes nothing and the planned date arriving
 * writes nothing — the next state written is `session_live`, by the auditee opening the
 * session. So the row's own derived facts answer instead, and *Awaiting documents* stops
 * being printed against an audit whose client filed everything and is being shown *Begin
 * audit session* today.
 */
function State({
  state,
  auditDay,
  documentsFiled,
}: {
  state: string;
  /** `audits.audit_day` — the server's reading of the planned date. */
  auditDay: boolean;
  /**
   * `audits.documents_filed`. Null before the plan is issued and the client written to.
   *
   * ⚠ It used to be `all_documents_in` — *every requested slot has an upload*. ADR 0025
   * unbinds evidence from clauses, so there is no set to complete and no readiness to earn.
   */
  documentsFiled: boolean | null;
}) {
  if (state === "awaiting_documents" || state === "session_scheduled") {
    if (auditDay) return <span className="pill p-ok">Audit day</span>;
    if (documentsFiled) return <span className="pill p-wait">Documents filed</span>;
    return <span className="pill p-wait">Awaiting documents</span>;
  }

  switch (state) {
    case "draft":
      return <span className="pill p-mute">Draft intake</span>;
    case "intake_complete":
      return <span className="pill p-wait">Intake complete</span>;
    case "determination_pending":
      return <span className="pill p-wait">Awaiting approval</span>;
    case "determination_confirmed":
      return <span className="pill p-wait">Determination confirmed</span>;
    case "plan_generated":
      return <span className="pill p-wait">Plan generated</span>;
    case "plan_approved":
      return <span className="pill p-wait">Plan approved</span>;
    case "session_live":
      return <span className="pill p-ok">Audit live</span>;
    case "paused":
      return <span className="pill p-wait">Paused</span>;
    case "session_closed":
      return <span className="pill p-ok">Session closed</span>;
    case "report_drafted":
      return <span className="pill p-wait">Report drafted</span>;
    case "report_under_review":
      return <span className="pill p-wait">Report under review</span>;
    case "report_approved":
      return <span className="pill p-ok">Report approved</span>;
    case "report_issued":
      return <span className="pill p-ok">Report issued</span>;
    case "closed":
      return <span className="pill p-mute">Closed</span>;
    case "reconfirmation":
      return <span className="pill p-red">Re-confirmation required</span>;
    case "cancelled":
      return <span className="pill p-mute">Cancelled</span>;
    default:
      return <span className="pill p-mute">{state}</span>;
  }
}

/**
 * One party's Step 2 approval. Absent is "pending", never blank.
 *
 * A blank cell would be read as "not applicable", and both approvals always apply — an
 * audit cannot proceed on one of them.
 */
function Approval({ party, at }: { party: string; at: string | null }) {
  return at ? (
    <span className="pill p-ok">{party} approved</span>
  ) : (
    <span className="pill p-wait">{party} pending</span>
  );
}

/**
 * The Restart tick — testing only, and the whole of the control in front of it.
 *
 * **It stops the click reaching the row.** Every other cell on this screen opens the audit,
 * and a checkbox that also navigated would take the reader somewhere else at the moment they
 * changed a setting — with no way to tell whether the change landed.
 *
 * **Optimistic, and it puts the value back if the server refuses.** The alternative is a
 * checkbox that does not move until a round trip finishes, which reads as broken; the
 * alternative to the revert is a screen that says the audit is nominated when it is not,
 * which is worse than either on this particular flag.
 *
 * The wording under it is not decoration. Whoever ticks this is granting the destruction of
 * a transcript, every finding, the report and the audit's trail, and the row gives them no
 * other place to find that out.
 */
function Restart({
  audit,
  onChanged,
  onError,
}: {
  audit: AuditSummary;
  onChanged: (allowed: boolean) => void;
  onError: (message: string | null) => void;
}) {
  const [saving, setSaving] = useState(false);
  const allowed = audit.restart_allowed ?? false;

  const toggle = (next: boolean) => {
    setSaving(true);
    onError(null);
    onChanged(next);
    setRestartAllowed(audit.id, next)
      .then((state) => onChanged(state.restart_allowed))
      .catch((cause: unknown) => {
        onChanged(allowed);
        onError(
          cause instanceof ApiError
            ? cause.message
            : "The restart permission could not be saved.",
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <label
      className="restart-tick"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      title={
        allowed
          ? "The lead auditor may restart this audit day. Doing so destroys the session transcript, every finding, the report and this audit's trail."
          : "Allow the lead auditor to restart this audit day for testing."
      }
    >
      <input
        type="checkbox"
        checked={allowed}
        disabled={saving}
        onChange={(event) => toggle(event.target.checked)}
      />
      <span className="sub">{allowed ? "Allowed" : "Not allowed"}</span>
    </label>
  );
}

/**
 * The Delete control — stops the click reaching the row, the same reason Restart's does,
 * and opens the confirmation dialog rather than acting on the click itself.
 */
function Delete({
  audit,
  onDeleted,
  onError,
}: {
  audit: AuditSummary;
  onDeleted: () => void;
  onError: (message: string | null) => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <>
      <button
        type="button"
        className="btn danger sm"
        onClick={(event) => {
          event.stopPropagation();
          setConfirming(true);
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        Delete
      </button>
      {confirming ? (
        <DeleteAuditDialog
          audit={audit}
          onCancel={() => setConfirming(false)}
          onDeleted={() => {
            setConfirming(false);
            onDeleted();
          }}
          onError={onError}
        />
      ) : null}
    </>
  );
}

/**
 * The typed reference is the whole of the control — no second checkbox, no cooling-off
 * period, the same posture Restart's tick takes. `matches` compares against the audit's own
 * reference rather than any close form of it, because a delete that could be triggered by a
 * guess is not a confirmation.
 */
function DeleteAuditDialog({
  audit,
  onCancel,
  onDeleted,
  onError,
}: {
  audit: AuditSummary;
  onCancel: () => void;
  onDeleted: () => void;
  onError: (message: string | null) => void;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const reference = audit.reference ?? "";
  const matches = reference !== "" && typed.trim() === reference;

  const confirm = async () => {
    setBusy(true);
    onError(null);
    try {
      await deleteAudit(audit.id, typed.trim());
      onDeleted();
    } catch (cause) {
      onError(cause instanceof ApiError ? cause.message : "The audit could not be deleted.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="scrim"
      role="presentation"
      // The row underneath this dialog is itself a click target (`open(audit)`, screen
      // 23's "rowlink"), and this dialog is nested inside that row's `<tr>` rather than
      // rendered elsewhere in the tree. Without this, every click inside the dialog —
      // typing into the reference field included — bubbled past the modal and the scrim
      // and reached the row's own `onClick`, navigating away mid-confirmation without
      // deleting anything.
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true">
        <h2>Delete this audit</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          Are you sure you want to delete audit <b>{reference}</b> - {audit.client_organisation}?
          <br />
          <i>NOTE: This action cannot be undone.</i>
          <br />
          Type the reference to confirm.
        </p>

        <TextField
          id="delete-audit-reference"
          label="Audit reference"
          value={typed}
          onChange={setTyped}
          placeholder={reference}
          required
        />

        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn danger"
            disabled={busy || !matches}
            onClick={() => void confirm()}
          >
            {busy ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
